/**
 * Workbook Risk Panel Component
 * Affiche les risques du classeur et les recommandations
 */

import React, { useState } from 'react';
import { LoadingSpinner } from '../common/LoadingSpinner';
import type { WorkbookRiskReport, WorkbookRisk } from '@excel-ai-studio/shared';
import { callAnalyzeAPI } from '../../services/workbook-brain-api.service';
import { createRiskMitigationSheet } from '../../services/workbook-brain-writer.service';

interface WorkbookRiskPanelProps {
  onAnalysisComplete?: (report: WorkbookRiskReport) => void;
}

export const WorkbookRiskPanel: React.FC<WorkbookRiskPanelProps> = ({ onAnalysisComplete }) => {
  const [report, setReport] = useState<WorkbookRiskReport | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterSeverity, setFilterSeverity] = useState<'all' | 'critical' | 'high' | 'medium' | 'low'>('all');
  const [expandedRisk, setExpandedRisk] = useState<number | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const analyzeRisks = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await callAnalyzeAPI({
        includeScanning: true,
        includeRiskAnalysis: true,
        includeDependencies: true,
      });

      if (!result.riskReport) {
        throw new Error('Aucun rapport de risques retourné par le serveur');
      }

      setReport(result.riskReport);
      onAnalysisComplete?.(result.riskReport);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible d\'analyser les risques');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateMitigationSheet = async () => {
    if (!report) return;
    
    try {
      setMessage(null);
      await createRiskMitigationSheet(report.risks);
      setMessage({ type: 'success', text: 'Feuille de mitigation créée avec succès' });
      setTimeout(() => setMessage(null), 5000);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Erreur lors de la création de la feuille';
      setMessage({ type: 'error', text: errorMsg });
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const handleExportToClipboard = async () => {
    if (!report) return;
    const lines = [
      `Rapport de risques — Score : ${report.riskScore}/100`,
      `Résumé : ${report.summary}`,
      `Critique: ${report.criticalCount} | Élevé: ${report.highCount} | Moyen: ${report.mediumCount} | Faible: ${report.lowCount}`,
      '',
      ...report.risks.map(
        (r) =>
          `[${r.severity.toUpperCase()}] ${r.category}${r.sheetName ? ` (${r.sheetName})` : ''} — ${r.description} | Recommandation: ${r.recommendation}${r.affectedCells !== undefined ? ` | Cellules: ${r.affectedCells}` : ''}`
      ),
    ];
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setMessage({ type: 'success', text: 'Rapport copié dans le presse-papiers' });
      setTimeout(() => setMessage(null), 3000);
    } catch {
      setMessage({ type: 'error', text: 'Impossible de copier dans le presse-papiers' });
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const getSeverityColor = (severity: string): string => {
    switch (severity) {
      case 'critical': return '#d32f2f';
      case 'high': return '#f57c00';
      case 'medium': return '#fbc02d';
      case 'low': return '#388e3c';
      default: return '#1976d2';
    }
  };

  const getSeverityIcon = (severity: string): string => {
    switch (severity) {
      case 'critical': return '🔴';
      case 'high': return '🟠';
      case 'medium': return '🟡';
      case 'low': return '🟢';
      default: return '⚪';
    }
  };

  const getSeverityLabel = (severity: string): string => {
    switch (severity) {
      case 'all': return 'Tous';
      case 'critical': return 'Critique';
      case 'high': return 'Élevé';
      case 'medium': return 'Moyen';
      case 'low': return 'Faible';
      default: return severity;
    }
  };

  const getFilteredRisks = (): WorkbookRisk[] => {
    if (!report) return [];
    if (filterSeverity === 'all') return report.risks;
    return report.risks.filter((r) => r.severity === filterSeverity);
  };

  if (isLoading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <LoadingSpinner label="Analyse des risques en cours..." />
      </div>
    );
  }

  const filteredRisks = getFilteredRisks();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* En-tête */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0 }}>⚠️ Analyse des risques</h3>
        <button
          onClick={analyzeRisks}
          style={{ padding: '8px 16px', backgroundColor: '#2196F3', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
        >
          Analyser les risques
        </button>
      </div>

      {error && (
        <div style={{ padding: '12px', backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px', fontSize: '13px' }}>
          ❌ {error}
        </div>
      )}

      {message && (
        <div style={{ padding: '12px', backgroundColor: message.type === 'success' ? '#e8f5e9' : '#ffebee', color: message.type === 'success' ? '#2e7d32' : '#c62828', borderRadius: '4px', fontSize: '13px' }}>
          {message.text}
        </div>
      )}

      {/* Carte du score de risque */}
      {report && (
        <div style={{
          padding: '16px',
          backgroundColor: '#f5f5f5',
          borderRadius: '4px',
          border: `3px solid ${getSeverityColor(
            report.riskScore >= 80 ? 'critical' : report.riskScore >= 50 ? 'high' : report.riskScore >= 20 ? 'medium' : 'low'
          )}`,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>Score de risque</div>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#333' }}>
                {report.riskScore}/100
              </div>
            </div>
            <div style={{ textAlign: 'right', fontSize: '12px', color: '#666' }}>
              <div>🔴 Critique : <strong>{report.criticalCount}</strong></div>
              <div>🟠 Élevé : <strong>{report.highCount}</strong></div>
              <div>🟡 Moyen : <strong>{report.mediumCount}</strong></div>
              <div>🟢 Faible : <strong>{report.lowCount}</strong></div>
            </div>
          </div>
          <div style={{ marginTop: '12px', padding: '8px', backgroundColor: 'white', borderRadius: '4px', fontSize: '12px', color: '#666' }}>
            {report.summary}
          </div>
          <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
            <button
              onClick={handleCreateMitigationSheet}
              style={{
                flex: 1,
                padding: '8px 12px',
                backgroundColor: '#d32f2f',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 'bold',
              }}
            >
              Créer feuille de mitigation
            </button>
            <button
              onClick={handleExportToClipboard}
              style={{
                padding: '8px 12px',
                backgroundColor: '#616161',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 'bold',
              }}
            >
              Copier
            </button>
          </div>
        </div>
      )}

      {/* Boutons de filtre */}
      {report && (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {(['all', 'critical', 'high', 'medium', 'low'] as const).map((severity) => (
            <button
              key={severity}
              onClick={() => setFilterSeverity(severity)}
              style={{
                padding: '6px 12px',
                backgroundColor: filterSeverity === severity ? getSeverityColor(severity) : '#f0f0f0',
                color: filterSeverity === severity ? 'white' : '#666',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: filterSeverity === severity ? 'bold' : 'normal',
              }}
            >
              {getSeverityLabel(severity)}
            </button>
          ))}
        </div>
      )}

      {/* Liste des risques */}
      {report && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {filteredRisks.length > 0 ? (
            filteredRisks.map((risk, idx) => (
              <div
                key={idx}
                style={{
                  padding: '12px',
                  backgroundColor: 'white',
                  borderLeft: `4px solid ${getSeverityColor(risk.severity)}`,
                  borderRadius: '4px',
                  transition: 'all 0.2s',
                  border: expandedRisk === idx ? `2px solid ${getSeverityColor(risk.severity)}` : 'none',
                  borderLeftWidth: '4px',
                }}
              >
                <div 
                  onClick={() => setExpandedRisk(expandedRisk === idx ? null : idx)}
                  style={{
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'start',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '16px' }}>{getSeverityIcon(risk.severity)}</span>
                      <span style={{ fontWeight: 'bold', fontSize: '13px', color: '#333' }}>{risk.category}</span>
                      {risk.sheetName && (
                        <span style={{ fontSize: '11px', backgroundColor: '#e3f2fd', color: '#1976d2', padding: '2px 6px', borderRadius: '3px' }}>
                          {risk.sheetName}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                      {risk.description}
                    </div>
                  </div>
                  <span style={{ fontSize: '18px', marginLeft: '8px' }}>
                    {expandedRisk === idx ? '▼' : '▶'}
                  </span>
                </div>

                {expandedRisk === idx && (
                  <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #eee', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#999', marginBottom: '4px' }}>
                        RECOMMANDATION
                      </div>
                      <div style={{ fontSize: '12px', color: '#333' }}>{risk.recommendation}</div>
                    </div>
                    {risk.affectedCells !== undefined && (
                      <div>
                        <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#999', marginBottom: '4px' }}>
                          CELLULES AFFECTÉES
                        </div>
                        <div style={{ fontSize: '12px', color: '#333' }}>{risk.affectedCells.toLocaleString()}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div style={{ padding: '16px', textAlign: 'center', color: '#999', fontSize: '13px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
              ✅ Aucun risque trouvé dans cette catégorie
            </div>
          )}
        </div>
      )}
    </div>
  );
};
